import {
  PageContent,
  PageHeader,
  Stack,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@olytic/foundation';
import {
  type SchemaObject,
  SchemaObjectDetail,
  SchemaObjectList,
} from '@olytic/foundation/admin';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDocumentTitle } from '../documentTitle';
import { NotFoundPage } from '../NotFoundPage';
import { type ObjectKey, OBJECTS, SETUP_ROUTES } from '../navigation';
import { useCurrentUser } from '../auth/AuthProvider';
import { authenticatedFetch } from '../auth/authClient';
import { type ObjectField, OBJECT_DEFS, schemaObjectFields } from '../objects/objectDefs';
import { objectMetaUrl, parseObjectMetaResponse } from '../objects/objectsApi';
import { HistoryTrackingPanel } from './HistoryTrackingPanel';
import { SetupHomeOverview } from './setupHome';

/** Setup: the admin side of the app, with its own navigation (AppShell). Users are in SetupUsers.tsx. */

export function SetupPage({
  title,
  eyebrow = 'Setup',
  children,
}: {
  title: string;
  eyebrow?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <PageContent as="section" width="wide">
      <Stack>
        <PageHeader className="border-0 bg-transparent px-0 pt-0 pb-0" eyebrow={eyebrow} title={title} />
        {children}
      </Stack>
    </PageContent>
  );
}

/* ── /setup ────────────────────────────────────────────────────── */

export function SetupHomePage(): JSX.Element {
  useDocumentTitle('Setup');
  const { role } = useCurrentUser();

  return (
    <SetupPage title="Setup home">
      <SetupHomeOverview role={role} />
    </SetupPage>
  );
}

/* ── /setup/objects ────────────────────────────────────────────── */

const schemaObjects: SchemaObject[] = OBJECTS.map((o) => {
  const def = OBJECT_DEFS[o.key];
  return {
    key: o.key,
    label: def.label,
    description: def.description,
    table: o.key.replace(/-/g, '_'),
    fieldCount: def.fields.length,
  };
});

const objectPath = (key: string) => `${SETUP_ROUTES.objects}/${key}`;

/**
 * The fields of the objects described by the schema, read from /meta (their
 * definitions name none): by object key, once each has answered.
 */
function useSchemaFields(keys: readonly ObjectKey[]): ReadonlyMap<ObjectKey, ObjectField[]> {
  const [fields, setFields] = useState<ReadonlyMap<ObjectKey, ObjectField[]>>(new Map());
  const wanted = keys.filter((key) => OBJECT_DEFS[key].schema).join(',');
  useEffect(() => {
    const controller = new AbortController();
    for (const key of wanted.split(',').filter(Boolean) as ObjectKey[]) {
      authenticatedFetch(objectMetaUrl(key), { signal: controller.signal })
        .then(parseObjectMetaResponse)
        .then((meta) => {
          if (!controller.signal.aborted) setFields((current) => new Map(current).set(key, schemaObjectFields(meta)));
        })
        .catch(() => {});
    }
    return () => controller.abort();
  }, [wanted]);
  return fields;
}

export function SetupObjectsPage(): JSX.Element {
  const navigate = useNavigate();
  const loaded = useSchemaFields(OBJECTS.map((o) => o.key));
  const objects = schemaObjects.map((o) => (loaded.has(o.key as ObjectKey) ? { ...o, fieldCount: loaded.get(o.key as ObjectKey)!.length } : o));
  return (
    <PageContent as="section" width="wide">
      <SchemaObjectList objects={objects} hrefFor={(o) => objectPath(o.key)} onOpen={(o) => navigate(objectPath(o.key))} />
    </PageContent>
  );
}

export function SetupObjectDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { key } = useParams();
  const object = schemaObjects.find((o) => o.key === key);
  if (!object) return <NotFoundPage />;
  const def = OBJECT_DEFS[object.key as keyof typeof OBJECT_DEFS];
  const fields = useSchemaFields([def.key]).get(def.key) ?? def.fields;

  return (
    <SetupPage title={object.label} eyebrow="Objects">
      <Tabs className="flex flex-col gap-5" defaultValue="fields">
        <TabList>
          <Tab value="fields">Fields</Tab>
          <Tab value="history">History tracking</Tab>
        </TabList>
        <TabPanel value="fields">
          <SchemaObjectDetail
            object={object}
            fields={fields.map((f) => ({
              name: f.name,
              type: f.type,
              ...(f.name === 'id' ? { primaryKey: true } : {}),
              ...(f.references ? { references: { key: f.references, label: OBJECT_DEFS[f.references].label } } : {}),
            }))}
            onOpenReference={(ref) => navigate(objectPath(ref))}
          />
        </TabPanel>
        <TabPanel value="history">
          <HistoryTrackingPanel object={object.key} />
        </TabPanel>
      </Tabs>
    </SetupPage>
  );
}
